/**
 * Status sets behind the board detail page's status filter.
 *
 * These live here rather than inside the page so the board page, the prefetch
 * hook and the tests all read the same lists - the three drifted apart when
 * each kept its own copy.
 *
 * The one non-obvious rule: the "All" filter is not every status. It omits
 * `completed`, because "All" is the browsing feed and finished work would crowd
 * out live feedback. `closed` and every active status stay, so "All" does not
 * silently collapse into "Active". The Resolved filter is where completed posts
 * are found, and a Custom selection is always passed through as the user set it.
 */

export type PostStatus =
  | 'open'
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'closed';

export type StatusFilter = 'active' | 'resolved' | 'all' | 'custom';

export const activeStatuses: PostStatus[] = [
  'open',
  'under_review',
  'planned',
  'in_progress',
];

export const resolvedStatuses: PostStatus[] = ['completed', 'closed'];

/** Every status, for the Custom picker's checkbox list. */
export const allStatuses: PostStatus[] = [
  ...activeStatuses,
  ...resolvedStatuses,
];

/** What the "All" filter actually queries: everything except `completed`. */
export const allFilterStatuses: PostStatus[] = [...activeStatuses, 'closed'];

/**
 * The status list to send to the posts query for a given filter selection.
 * Filtering happens in the query rather than on the fetched array, so
 * pagination and page size stay correct.
 */
export function getSelectedStatuses(
  filter: StatusFilter,
  customStatuses: string[] = [],
): string[] {
  switch (filter) {
    case 'active':
      return [...activeStatuses];
    case 'resolved':
      return [...resolvedStatuses];
    case 'custom':
      return [...customStatuses];
    case 'all':
    default:
      return [...allFilterStatuses];
  }
}
